/**
 * Shared conversions between a `recurring_order_templates` row (+ its
 * `recurring_order_template_legs`) and the `OrderFormData` shape used by
 * `OrderFormDialog` in `templateEditMode`. Also exports the row-shape
 * builders used by both the create path (`useOrderMutations`) and the
 * edit path (RecurringOrdersSubTable + RecurringTemplateDetail) so those
 * two flows stay in lock-step and multi-leg edits round-trip correctly.
 */

import type { Location } from "@/types/tms";
import type { OrderFormData } from "@/components/orders/orderFormTypes";
import { initialFormData } from "@/components/orders/orderFormTypes";
import { LEGACY_TEMPLATE_TZ, PLANNING_TZ, utcToFormLocal, wallClockToUtc } from "@/lib/timezone";

/**
 * Sentinel stored in `recurring_order_templates.end_date` for templates that
 * never end. The column is NOT NULL, so an open-ended recurrence is encoded
 * as this far-future date rather than as NULL.
 */
export const OPEN_ENDED_TEMPLATE_END_DATE = "2099-12-31";

/** True when a template row's `end_date` means "doesn't end". */
export function isOpenEndedTemplate(endDate: string | null | undefined): boolean {
  return !endDate || endDate >= OPEN_ENDED_TEMPLATE_END_DATE;
}

/**
 * Recurring template timezones (ASE-17444, docs/TIMEZONES.md).
 *
 * A template's `departure_time` / `start_date` / `recurrence_days` are wall-
 * clock values in the template's own `timezone`. New templates are Central.
 * Templates created before ASE-17444 are Pacific and keep generating exactly
 * as before until converted by being edited and saved through the form, which
 * always saves Central. Conversion preserves
 * the real pickup instant. Mirrors `_shared/planning-tz.ts` for the generator.
 */
export { LEGACY_TEMPLATE_TZ };

/** A template's timezone; missing/unknown values are legacy Pacific so an old
 *  row is never silently reinterpreted two hours off. */
export function templateTimezone(value: string | null | undefined): string {
  return value === PLANNING_TZ || value === LEGACY_TEMPLATE_TZ ? value : LEGACY_TEMPLATE_TZ;
}

export function isLegacyTemplate(template: { timezone?: string | null }): boolean {
  return templateTimezone(template.timezone) !== PLANNING_TZ;
}


export interface TemplateSchedule {
  departure_time: string;
  start_date: string;
  end_date: string;
  recurrence_days: number[];
  timezone?: string | null;
}

/**
 * The same schedule expressed in Central. For a Central template this is the
 * identity. For a Pacific template the departure moves +2h (same real
 * instant); if that crosses midnight, the start date, end date and weekdays
 * all move one day forward so every occurrence keeps its real pickup time.
 */
export function templateScheduleInPlanningTz(t: TemplateSchedule): Required<Omit<TemplateSchedule, "timezone">> & {
  timezone: string;
  dayShift: number;
} {
  const tz = templateTimezone(t.timezone);
  const dep = (t.departure_time || "08:00").slice(0, 5);
  if (tz === PLANNING_TZ) {
    return { departure_time: dep, start_date: t.start_date, end_date: t.end_date, recurrence_days: [...t.recurrence_days], timezone: PLANNING_TZ, dayShift: 0 };
  }
  const utc = wallClockToUtc(t.start_date, dep, tz);
  if (!utc) throw new Error(`Invalid template schedule: ${t.start_date} ${dep}`);
  const [date, time] = utcToFormLocal(utc).split("T");
  const dayShift = Math.round((Date.parse(`${date}T00:00:00Z`) - Date.parse(`${t.start_date}T00:00:00Z`)) / 86_400_000);
  const shiftDate = (d: string) =>
    isOpenEndedTemplate(d) ? d : new Date(Date.parse(`${d}T00:00:00Z`) + dayShift * 86_400_000).toISOString().slice(0, 10);
  return {
    departure_time: time,
    start_date: date,
    end_date: shiftDate(t.end_date),
    recurrence_days: t.recurrence_days.map((d) => (((d + dayShift) % 7) + 7) % 7).sort((a, b) => a - b),
    timezone: PLANNING_TZ,
    dayShift,
  };
}

export interface TemplateLegRow {
  id: string;
  template_id: string;
  tenant_id: string;
  leg_sequence: number;
  ship_from_location_id: string | null;
  ship_from_street_address: string | null;
  ship_from_city: string | null;
  ship_from_province_state: string | null;
  ship_from_postal_zip_code: string | null;
  ship_from_country: string | null;
  ship_to_location_id: string | null;
  ship_to_street_address: string | null;
  ship_to_city: string | null;
  ship_to_province_state: string | null;
  ship_to_postal_zip_code: string | null;
  ship_to_country: string | null;
  start_offset_minutes: number;
  trip_duration_minutes: number;
  ready_window_minutes: number;
  delivery_window_minutes: number;
  trip_type: string | null;
  trailer_type_id: string | null;
  trailer_id: string | null;
  assigned_tractor_id: string | null;
  assigned_operator_id: string | null;
  assigned_operator_2_id: string | null;
  origin_shuttle_carrier_name?: string | null;
  origin_shuttle_carrier_dot?: string | null;
  destination_shuttle_carrier_name?: string | null;
  destination_shuttle_carrier_dot?: string | null;
}

export interface TemplateRow {
  id: string;
  billing_party_id: string | null;
  ship_from_location_id: string | null;
  ship_to_location_id: string | null;
  ship_from_street_address: string | null;
  ship_from_city: string | null;
  ship_from_province_state: string | null;
  ship_from_postal_zip_code: string | null;
  ship_from_country: string | null;
  ship_to_street_address: string | null;
  ship_to_city: string | null;
  ship_to_province_state: string | null;
  ship_to_postal_zip_code: string | null;
  ship_to_country: string | null;
  departure_time: string;
  trip_duration_minutes: number;
  recurrence_days: number[];
  start_date: string;
  end_date: string;
  trailer_type_id: string | null;
  trailer_id: string | null;
  trip_type: string | null;
  tender_id: string | null;
  assigned_tractor_id: string | null;
  assigned_operator_id: string | null;
  assigned_operator_2_id: string | null;
  ready_window_minutes: number;
  delivery_window_minutes: number;
  order_type: string;
  use_same_trip_type_for_all_legs: boolean;
  /** IANA zone the schedule fields are in; see `templateTimezone`. */
  timezone?: string | null;
}

function pad(n: number) {
  return String(n).padStart(2, "0");
}
function fmtLocalNaive(d: Date) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * Build an `OrderFormData` from a template row plus (optional) its
 * template_legs. Multi-leg templates return `order_type = "multi-leg"` with
 * a fully populated `legs[]`. Single-leg templates return the single-leg
 * shape.
 *
 * All datetime strings are Central wall-clock `YYYY-MM-DDTHH:mm` (the order
 * form is always Central); legacy Pacific templates are converted on the way
 * in, preserving the real pickup time. The
 * `start_date` anchors the display so the first occurrence is meaningful;
 * the template's actual generation cadence is separately encoded in
 * `recurrence_days`.
 */
export function templateToOrderFormData(
  template: TemplateRow,
  legs: TemplateLegRow[] | null | undefined,
  locations: Location[] | undefined,
): OrderFormData {
  const schedule = templateScheduleInPlanningTz({
    departure_time: template.departure_time || "08:00",
    start_date: template.start_date || new Date().toISOString().slice(0, 10),
    end_date: template.end_date,
    recurrence_days: template.recurrence_days || [],
    timezone: template.timezone,
  });
  const startDate = schedule.start_date;
  const dep = schedule.departure_time;
  const [hh, mm] = dep.split(":").map((n) => parseInt(n, 10));
  const anchor = new Date(`${startDate}T${dep}:00`);
  const ready = fmtLocalNaive(anchor);
  const readyEnd = fmtLocalNaive(new Date(anchor.getTime() + (template.ready_window_minutes || 0) * 60_000));
  const deliveryAnchor = new Date(anchor.getTime() + (template.trip_duration_minutes || 0) * 60_000);
  const delivery = fmtLocalNaive(deliveryAnchor);
  const deliveryEnd = fmtLocalNaive(new Date(deliveryAnchor.getTime() + (template.delivery_window_minutes || 0) * 60_000));

  const fromLoc = locations?.find((l) => l.id === template.ship_from_location_id);
  const toLoc = locations?.find((l) => l.id === template.ship_to_location_id);

  const isMultiLeg =
    (template.order_type || "single-leg") === "multi-leg" && (legs?.length ?? 0) > 0;

  const base: OrderFormData = {
    ...initialFormData,
    order_type: isMultiLeg ? "multi-leg" : "single-leg",
    status: "booked",
    billing_party_id: template.billing_party_id || "",
    is_recurring: true,
    recurrence_days: schedule.recurrence_days,
    recurrence_end_date: isOpenEndedTemplate(schedule.end_date) ? "" : schedule.end_date,
    trailer_type_id: template.trailer_type_id || "",
    trailer_id: template.trailer_id || "",
    trip_type: template.trip_type || "",
    tender_id: template.tender_id || "",
    assigned_tractor_id: template.assigned_tractor_id || "",
    assigned_operator_id: template.assigned_operator_id || "",
    assigned_operator_2_id: template.assigned_operator_2_id || "",
    ship_from_location_id: template.ship_from_location_id || "",
    ship_from_street_address:
      template.ship_from_street_address || fromLoc?.street_address || "",
    ship_from_city: template.ship_from_city || fromLoc?.city || "",
    ship_from_province_state:
      template.ship_from_province_state || fromLoc?.province_state || "",
    ship_from_postal_zip_code:
      template.ship_from_postal_zip_code || fromLoc?.postal_zip_code || "",
    ship_from_country: template.ship_from_country || fromLoc?.country || "",
    ship_to_location_id: template.ship_to_location_id || "",
    ship_to_street_address:
      template.ship_to_street_address || toLoc?.street_address || "",
    ship_to_city: template.ship_to_city || toLoc?.city || "",
    ship_to_province_state:
      template.ship_to_province_state || toLoc?.province_state || "",
    ship_to_postal_zip_code:
      template.ship_to_postal_zip_code || toLoc?.postal_zip_code || "",
    ship_to_country: template.ship_to_country || toLoc?.country || "",
    ready_time: ready,
    ready_time_end: readyEnd,
    requested_delivery_time: delivery,
    requested_delivery_time_end: deliveryEnd,
    use_same_trip_type_for_all_legs: template.use_same_trip_type_for_all_legs ?? true,
    use_same_assets_for_all_legs: true,
    legs: [],
  };

  if (!isMultiLeg) return base;

  const sortedLegs = [...(legs || [])].sort((a, b) => a.leg_sequence - b.leg_sequence);
  const baseMs = anchor.getTime();
  base.legs = sortedLegs.map((tl) => {
    const legReadyAnchor = new Date(baseMs + (tl.start_offset_minutes ?? 0) * 60_000);
    const legDeliveryAnchor = new Date(
      legReadyAnchor.getTime() + (tl.trip_duration_minutes ?? 0) * 60_000,
    );
    const legReadyEnd = new Date(
      legReadyAnchor.getTime() + (tl.ready_window_minutes ?? 0) * 60_000,
    );
    const legDeliveryEnd = new Date(
      legDeliveryAnchor.getTime() + (tl.delivery_window_minutes ?? 0) * 60_000,
    );
    const legFrom = locations?.find((l) => l.id === tl.ship_from_location_id);
    const legTo = locations?.find((l) => l.id === tl.ship_to_location_id);
    return {
      kind: "leg" as const,
      ship_from_location_id: tl.ship_from_location_id || "",
      ship_from_street_address:
        tl.ship_from_street_address || legFrom?.street_address || "",
      ship_from_city: tl.ship_from_city || legFrom?.city || "",
      ship_from_province_state:
        tl.ship_from_province_state || legFrom?.province_state || "",
      ship_from_postal_zip_code:
        tl.ship_from_postal_zip_code || legFrom?.postal_zip_code || "",
      ship_from_country: tl.ship_from_country || legFrom?.country || "",
      ship_to_location_id: tl.ship_to_location_id || "",
      ship_to_street_address:
        tl.ship_to_street_address || legTo?.street_address || "",
      ship_to_city: tl.ship_to_city || legTo?.city || "",
      ship_to_province_state:
        tl.ship_to_province_state || legTo?.province_state || "",
      ship_to_postal_zip_code:
        tl.ship_to_postal_zip_code || legTo?.postal_zip_code || "",
      ship_to_country: tl.ship_to_country || legTo?.country || "",
      ready_time: fmtLocalNaive(legReadyAnchor),
      requested_delivery_time: fmtLocalNaive(legDeliveryAnchor),
      ready_time_end: fmtLocalNaive(legReadyEnd),
      requested_delivery_time_end: fmtLocalNaive(legDeliveryEnd),
      trailer_type_id: tl.trailer_type_id || "",
      trailer_id: tl.trailer_id || "",
      trip_type: tl.trip_type || "",
      assigned_tractor_id: tl.assigned_tractor_id || "",
      assigned_operator_id: tl.assigned_operator_id || "",
      assigned_operator_2_id: tl.assigned_operator_2_id || "",
    };
  });

  // A stored `trailer_id`/asset on the header + identical values on every leg
  // is the "same for all legs" case; if legs diverge we surface the per-leg
  // controls by flipping the toggle off.
  const firstLeg = base.legs[0];
  const sameAssets =
    !!firstLeg &&
    base.legs.every(
      (l) =>
        l.trailer_id === firstLeg.trailer_id &&
        l.trailer_type_id === firstLeg.trailer_type_id &&
        l.assigned_tractor_id === firstLeg.assigned_tractor_id &&
        l.assigned_operator_id === firstLeg.assigned_operator_id &&
        l.assigned_operator_2_id === firstLeg.assigned_operator_2_id,
    );
  base.use_same_assets_for_all_legs = sameAssets;

  return base;
}

/**
 * Header patch for `recurring_order_templates`. Preserves the historical
 * fields the update mutation was already writing (departure/duration/
 * windows/addresses/etc.) and additionally captures `order_type` +
 * `use_same_trip_type_for_all_legs` so single↔multi conversions round-trip.
 */
export function orderFormToTemplatePatch(fd: OrderFormData): Record<string, unknown> {
  const firstLeg = fd.order_type === "multi-leg" ? fd.legs[0] : null;
  const lastLeg =
    fd.order_type === "multi-leg" ? fd.legs[fd.legs.length - 1] : null;

  const headerReadyLocal =
    firstLeg?.ready_time || fd.ready_time || `${new Date().toISOString().slice(0, 10)}T08:00`;
  const headerReadyEndLocal =
    firstLeg?.ready_time_end || fd.ready_time_end || headerReadyLocal;
  const headerDeliveryLocal =
    lastLeg?.requested_delivery_time || fd.requested_delivery_time || headerReadyLocal;
  const headerDeliveryEndLocal =
    lastLeg?.requested_delivery_time_end ||
    fd.requested_delivery_time_end ||
    headerDeliveryLocal;

  const [readyDate, readyTime] = headerReadyLocal.split("T");
  const readyMs = new Date(`${headerReadyLocal}:00`).getTime();
  const readyEndMs = new Date(`${headerReadyEndLocal}:00`).getTime();
  const deliveryMs = new Date(`${headerDeliveryLocal}:00`).getTime();
  const deliveryEndMs = new Date(`${headerDeliveryEndLocal}:00`).getTime();

  const headerFrom =
    firstLeg || {
      ship_from_location_id: fd.ship_from_location_id,
      ship_from_street_address: fd.ship_from_street_address,
      ship_from_city: fd.ship_from_city,
      ship_from_province_state: fd.ship_from_province_state,
      ship_from_postal_zip_code: fd.ship_from_postal_zip_code,
      ship_from_country: fd.ship_from_country,
    };
  const headerTo =
    lastLeg || {
      ship_to_location_id: fd.ship_to_location_id,
      ship_to_street_address: fd.ship_to_street_address,
      ship_to_city: fd.ship_to_city,
      ship_to_province_state: fd.ship_to_province_state,
      ship_to_postal_zip_code: fd.ship_to_postal_zip_code,
      ship_to_country: fd.ship_to_country,
    };

  return {
    order_type: fd.order_type,
    use_same_trip_type_for_all_legs: fd.use_same_trip_type_for_all_legs ?? true,
    ship_from_location_id: (headerFrom as any).ship_from_location_id || null,
    ship_to_location_id: (headerTo as any).ship_to_location_id || null,
    ship_from_street_address: (headerFrom as any).ship_from_street_address || null,
    ship_from_city: (headerFrom as any).ship_from_city || null,
    ship_from_province_state: (headerFrom as any).ship_from_province_state || null,
    ship_from_postal_zip_code: (headerFrom as any).ship_from_postal_zip_code || null,
    ship_from_country: (headerFrom as any).ship_from_country || null,
    ship_to_street_address: (headerTo as any).ship_to_street_address || null,
    ship_to_city: (headerTo as any).ship_to_city || null,
    ship_to_province_state: (headerTo as any).ship_to_province_state || null,
    ship_to_postal_zip_code: (headerTo as any).ship_to_postal_zip_code || null,
    ship_to_country: (headerTo as any).ship_to_country || null,
    departure_time: (readyTime || "08:00").slice(0, 5),
    // The form is always Central, so saving a template always stores Central
    // (this is also how an edited legacy Pacific template gets converted).
    timezone: PLANNING_TZ,
    trip_duration_minutes: Math.max(1, Math.round((deliveryMs - readyMs) / 60_000)),
    recurrence_days: fd.recurrence_days,
    start_date: readyDate || new Date().toISOString().slice(0, 10),
    end_date: fd.recurrence_end_date || OPEN_ENDED_TEMPLATE_END_DATE,
    ready_window_minutes: Math.max(0, Math.round((readyEndMs - readyMs) / 60_000)),
    delivery_window_minutes: Math.max(0, Math.round((deliveryEndMs - deliveryMs) / 60_000)),
    trailer_type_id: fd.trailer_type_id || null,
    trailer_id: fd.trailer_id || null,
    trip_type: fd.trip_type || null,
    tender_id: fd.tender_id || null,
    assigned_tractor_id: fd.assigned_tractor_id || null,
    assigned_operator_id: fd.assigned_operator_id || null,
    assigned_operator_2_id: fd.assigned_operator_2_id || null,
  };
}

/**
 * Build `recurring_order_template_legs` upsert rows (one per form leg) from
 * `OrderFormData`. The generator reads these on the next run to materialize
 * multi-leg occurrences correctly.
 */
export function orderFormToTemplateLegRows(
  fd: OrderFormData,
  templateId: string,
  tenantId: string,
  locations: Location[] | undefined,
): Record<string, unknown>[] {
  if (fd.order_type !== "multi-leg" || fd.legs.length === 0) return [];
  const useSameAssets = fd.use_same_assets_for_all_legs !== false;
  const useSameTripType = fd.use_same_trip_type_for_all_legs !== false;
  const baseMs = new Date(`${fd.legs[0].ready_time}:00`).getTime();

  return fd.legs.map((leg, i) => {
    const legReadyMs = new Date(`${leg.ready_time}:00`).getTime();
    const legDeliveryMs = new Date(`${leg.requested_delivery_time}:00`).getTime();
    const legReadyEndMs = leg.ready_time_end
      ? new Date(`${leg.ready_time_end}:00`).getTime()
      : legReadyMs;
    const legDeliveryEndMs = leg.requested_delivery_time_end
      ? new Date(`${leg.requested_delivery_time_end}:00`).getTime()
      : legDeliveryMs;
    const fromLoc =
      leg.ship_from_location_id ||
      locations?.find(
        (l) =>
          l.street_address?.toLowerCase() === leg.ship_from_street_address?.toLowerCase() &&
          l.city?.toLowerCase() === leg.ship_from_city?.toLowerCase(),
      )?.id ||
      null;
    const toLoc =
      leg.ship_to_location_id ||
      locations?.find(
        (l) =>
          l.street_address?.toLowerCase() === leg.ship_to_street_address?.toLowerCase() &&
          l.city?.toLowerCase() === leg.ship_to_city?.toLowerCase(),
      )?.id ||
      null;
    return {
      template_id: templateId,
      tenant_id: tenantId,
      leg_sequence: i + 1,
      ship_from_location_id: fromLoc,
      ship_from_street_address: leg.ship_from_street_address,
      ship_from_city: leg.ship_from_city,
      ship_from_province_state: leg.ship_from_province_state || null,
      ship_from_postal_zip_code: leg.ship_from_postal_zip_code || null,
      ship_from_country: leg.ship_from_country || null,
      ship_to_location_id: toLoc,
      ship_to_street_address: leg.ship_to_street_address,
      ship_to_city: leg.ship_to_city,
      ship_to_province_state: leg.ship_to_province_state || null,
      ship_to_postal_zip_code: leg.ship_to_postal_zip_code || null,
      ship_to_country: leg.ship_to_country || null,
      start_offset_minutes: Math.max(0, Math.round((legReadyMs - baseMs) / 60_000)),
      trip_duration_minutes: Math.max(1, Math.round((legDeliveryMs - legReadyMs) / 60_000)),
      ready_window_minutes: Math.max(0, Math.round((legReadyEndMs - legReadyMs) / 60_000)),
      delivery_window_minutes: Math.max(
        0,
        Math.round((legDeliveryEndMs - legDeliveryMs) / 60_000),
      ),
      trip_type: (useSameTripType ? fd.trip_type || null : leg.trip_type || null) as any,
      trailer_type_id:
        (useSameAssets ? fd.trailer_type_id : leg.trailer_type_id) || null,
      trailer_id:
        (useSameAssets ? fd.trailer_id : leg.trailer_id || fd.trailer_id) || null,
      assigned_tractor_id:
        (useSameAssets ? fd.assigned_tractor_id : leg.assigned_tractor_id) || null,
      assigned_operator_id:
        (useSameAssets ? fd.assigned_operator_id : leg.assigned_operator_id) || null,
      assigned_operator_2_id:
        (useSameAssets ? fd.assigned_operator_2_id : leg.assigned_operator_2_id) || null,
    };
  });
}